import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { DEFAULT_FIGURE, type BodyColor, type BodyGender } from '../constants/bodyAtlas';
import { fetchUserPreferences, saveUserPreference } from '../lib/userPreferences';
import { accountGeneration, accountStorage, cachedUserIdOwner, scopeToAccount } from '../lib/accountStorage';

/**
 * Body-figure preference (Disciplines DS4.3). Which anatomical figure the Body
 * Atlas renders (Fitness › Body Map lens). A profile-level preference: it rides
 * the shared per-user `user_preferences` row (the `body_figure_gender` slice) so
 * it follows the user across devices — same load-on-login + partial-upsert
 * write-through as theme/language/start-day. The local zustand-persist cache is
 * the offline/first-paint fallback; the cloud value wins once initialize() runs.
 *
 * ⚠️⚠️ `gender` IS NULLABLE, AND NULL IS MEANINGFUL: it means the user has not
 * answered. It used to collapse to `'male'`, which made an unanswered question
 * indistinguishable from an answer of "male" — so the Atlas drew a male figure
 * for everyone who never opened Settings, and any feature gating on
 * `=== 'female'` would have been invisible to the people it is for while
 * looking like it worked.
 *
 * ⭐⭐ The DATABASE always kept the distinction (`body_figure_gender` is
 * nullable with no column default); only this store discarded it. Use
 * `selectFigureGender` when you need something to DRAW, and the raw `gender`
 * when you need to know what she actually said.
 */
interface BodyAtlasState {
  /** The user's answer. `null` = not answered yet. NEVER default this. */
  gender: BodyGender | null;
  /** Figure colourway — defaults to the desaturated 'mono' look. */
  bodyColor: BodyColor;
  /** User whose cloud value we've loaded (skip re-init for the same user). */
  initializedUserId: string | null;
  /** User who owns the locally cached value (so we don't apply it to another). */
  cachedUserId: string | null;
  initialize: () => Promise<void>;
  setGender: (gender: BodyGender) => void;
  setBodyColor: (bodyColor: BodyColor) => void;
}

export const useBodyAtlasStore = create<BodyAtlasState>()(
  persist(
    (set, get) => ({
      gender: null,
      bodyColor: 'mono',
      initializedUserId: null,
      cachedUserId: null,

      // Load-on-login from the shared user_preferences row (body_figure_*).
      initialize: async () => {
        const at = accountGeneration();
        const res = await fetchUserPreferences();
        // An answer for an account that has since signed out is not this copy's.
        if (at !== accountGeneration()) return;
        if (!res) return; // logged out
        if (get().initializedUserId === res.userId) return;
        set((state) => {
          const cacheIsOurs =
            state.cachedUserId === null || state.cachedUserId === res.userId;
          const cloudGender = res.row?.body_figure_gender;
          /* ⚠️ The final fallback is NULL, not 'male'. A cloud row with no
             answer means the question is still open, and that has to survive
             the trip through this store or the bug simply moves here. */
          const gender: BodyGender | null =
            cloudGender === 'male' || cloudGender === 'female'
              ? cloudGender
              : cacheIsOurs
              ? state.gender
              : null;
          const cloudColor = res.row?.body_figure_color;
          const bodyColor: BodyColor =
            cloudColor === 'blue' || cloudColor === 'mono'
              ? cloudColor
              : cacheIsOurs
              ? state.bodyColor
              : 'mono';
          return { gender, bodyColor, initializedUserId: res.userId, cachedUserId: res.userId };
        });
      },

      setGender: (gender) => {
        set({ gender });
        void saveUserPreference({ body_figure_gender: gender });
      },

      setBodyColor: (bodyColor) => {
        set({ bodyColor });
        void saveUserPreference({ body_figure_color: bodyColor });
      },
    }),
    {
      name: 'body-atlas-prefs',
      // ⭐ One saved copy per account — see `scopeToAccount` below.
      storage: accountStorage(cachedUserIdOwner),
      /* ⚠️⚠️ v1 → v2 DISCARDS THE CACHED GENDER, deliberately. Every v1 cache
         holds a concrete value, because v1's initial state was `'male'` and it
         persisted whether or not the user ever chose — so a cached `'male'`
         cannot be told apart from a chosen one. The cloud CAN tell them apart
         (it only ever received a value from setGender), so dropping the cache
         and re-reading is the one way to recover the truth.
         ⚠️ Cost: a user who is offline immediately after upgrading sees the
         default figure instead of their choice until `initialize()` runs. It
         heals itself on the next authenticated load. */
      version: 2,
      migrate: (persisted, version) => {
        const prev = (persisted ?? {}) as Partial<BodyAtlasState>;
        if (version < 2) return { ...prev, gender: null } as BodyAtlasState;
        return prev as BodyAtlasState;
      },
      partialize: (state) => ({
        gender: state.gender,
        bodyColor: state.bodyColor,
        cachedUserId: state.cachedUserId,
      }),
    }
  )
);

/* ⚠️⚠️ ONE SAVED COPY PER ACCOUNT (lib/accountStorage). The device kept one copy, tagged with the
   account it was loaded for, and a sign-out does not reload the page: the next account was drawn the
   last one's figure — and its answer, which gates the period tracker — until its own load, and kept it
   when its row had none. */
scopeToAccount(useBodyAtlasStore);

/**
 * The figure to DRAW — the answer if there is one, else the fallback.
 *
 * ⚠️ Use this for anything that renders or builds an asset path. Use the raw
 * `gender` only where "has she answered?" is the actual question (the toggles,
 * and any feature gated on the answer).
 */
export const selectFigureGender = (s: BodyAtlasState): BodyGender =>
  s.gender ?? DEFAULT_FIGURE;

/** Whether the user has actually answered the body-figure question. */
export const selectHasAnsweredGender = (s: BodyAtlasState): boolean =>
  s.gender !== null;
